import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ListFilter, Search } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import type { WorkflowRunStepInstancePayload } from './workflow-run.types';
import {
  formatDuration,
  runStatusLabel,
  WorkflowRunStatusBadge,
} from './workflow-run-status';
import {
  summarizeFanOutStatuses,
  type FanOutStatusCount,
} from './workflow-run-fan-out-group';
import { previewValue } from '@cotera/client/app/v1/components/for-each-item-label';

const ROOT_INSTANCE_KEY = 'root';

/** Sentinel filter value meaning "every status". */
export const INSTANCE_STATUS_FILTER_ALL = 'all';

/** Above this many instances, the list shows a search box. */
const SEARCH_VISIBLE_THRESHOLD = 10;

/** Fixed row height (px) for the virtualized instance list (two text lines). */
const INSTANCE_ROW_HEIGHT = 52;

const FAILURE_STATUSES = new Set(['failed', 'errored']);

export type InstanceStatusFilterOption = {
  value: string;
  label: string;
  count: number;
};

/**
 * The status filter options: "All" (total) followed by one per present status
 * (failures first, from {@link summarizeFanOutStatuses}'s ordering), each with
 * its count — so the dropdown doubles as the status breakdown.
 */
export function buildStatusFilterOptions(
  statusCounts: readonly FanOutStatusCount[],
  total: number
): InstanceStatusFilterOption[] {
  return [
    { value: INSTANCE_STATUS_FILTER_ALL, label: 'All', count: total },
    ...statusCounts.map((entry) => ({
      value: entry.status,
      label: runStatusLabel(entry.status),
      count: entry.count,
    })),
  ];
}

function toDate(value: Date | string | null): Date | null {
  if (value === null) {
    return null;
  }
  return value instanceof Date ? value : new Date(value);
}

/**
 * Status filter behind a filter icon: a dropdown of radio options ("All" plus
 * each present status with its count), so the control stays compact and the
 * breakdown is one click away. The trigger shows the active status label when a
 * filter is applied.
 */
export const InstanceStatusFilter: React.FC<{
  statusCounts: readonly FanOutStatusCount[];
  total: number;
  active: string;
  onChange: (value: string) => void;
}> = ({ statusCounts, total, active, onChange }) => {
  const options = buildStatusFilterOptions(statusCounts, total);
  const activeLabel =
    active === INSTANCE_STATUS_FILTER_ALL ? null : runStatusLabel(active);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Filter by status"
          aria-label="Filter by status"
          className={cn(
            'flex h-7 shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 text-xs transition-colors hover:bg-muted/60',
            activeLabel !== null ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          <ListFilter className="size-3.5" />
          {activeLabel !== null ? (
            <span className="font-medium">{activeLabel}</span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        <DropdownMenuRadioGroup value={active} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              <span className="flex-1">{option.label}</span>
              <span className="ml-4 tabular-nums text-muted-foreground">
                {option.count}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Unwrap server-persisted step input wrappers (`{ t: 'single-parent' | 'fan-in', data }`)
 * so the label shows the actual input value rather than the discriminator string.
 */
function unwrapStepInput(input: unknown): unknown {
  if (
    input !== null &&
    typeof input === 'object' &&
    !Array.isArray(input) &&
    't' in input &&
    'data' in input
  ) {
    const record = input as Record<string, unknown>;
    if (record['t'] === 'single-parent' || record['t'] === 'fan-in') {
      return record['data'];
    }
  }
  return input;
}

export function labelForStepInstance(
  instance: WorkflowRunStepInstancePayload,
  index: number,
  all: readonly WorkflowRunStepInstancePayload[]
): string {
  if (instance.instanceKey === ROOT_INSTANCE_KEY) {
    const childCount = all.filter(
      (row) => row.parentInstanceKey === instance.instanceKey
    ).length;
    return childCount > 0 ? `All items (${childCount})` : 'Root';
  }
  if (instance.parentInstanceKey !== null) {
    const siblings = all.filter(
      (row) => row.parentInstanceKey === instance.parentInstanceKey
    );
    const siblingIndex = siblings.findIndex(
      (row) => row.instanceKey === instance.instanceKey
    );
    const n = siblingIndex >= 0 ? siblingIndex + 1 : index + 1;
    // Prefer the item's input for the label — it identifies *which* item this
    // is — falling back to the output when there is no meaningful input. Use the
    // item's own name as the label when we have one, only falling back to the
    // generic "Item N" when there's nothing meaningful to show.
    const preview =
      previewValue(unwrapStepInput(instance.input)) ??
      previewValue(instance.output);
    return preview !== null ? preview : `Item ${n}`;
  }
  return `Instance ${index + 1}`;
}

export function defaultSelectedInstanceKey(
  instances: readonly WorkflowRunStepInstancePayload[]
): string | null {
  if (instances.length === 0) {
    return null;
  }
  const root = instances.find(
    (instance) => instance.instanceKey === ROOT_INSTANCE_KEY
  );
  const hasChildren = instances.some(
    (instance) => instance.parentInstanceKey !== null
  );
  // For-each parent + children: open on the parent (full items array).
  if (root !== undefined && hasChildren) {
    return root.instanceKey;
  }
  if (root !== undefined && instances.length === 1) {
    return root.instanceKey;
  }
  // Fan-out body nodes often have no root — open the first iteration.
  const firstChild = instances.find(
    (instance) => instance.instanceKey !== ROOT_INSTANCE_KEY
  );
  return firstChild?.instanceKey ?? instances[0]?.instanceKey ?? null;
}

type LabeledInstance = {
  instance: WorkflowRunStepInstancePayload;
  label: string;
};

/** One virtualized row: status dot, label, badge, then duration + any error. */
const InstanceRow: React.FC<{
  row: LabeledInstance;
  selected: boolean;
  onSelect: (instanceKey: string) => void;
}> = ({ row, selected, onSelect }) => {
  const { instance, label } = row;
  const isFailed = FAILURE_STATUSES.has(instance.status);
  const duration = formatDuration(
    toDate(instance.startedAt),
    toDate(instance.finishedAt)
  );
  return (
    <button
      type="button"
      onClick={() => onSelect(instance.instanceKey)}
      className={cn(
        'flex h-full w-full flex-col justify-center gap-0.5 rounded-md px-2.5 text-left transition-colors',
        selected ? 'bg-primary/10' : 'hover:bg-muted/60'
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-sm font-medium',
            selected ? 'text-foreground' : 'text-foreground/90'
          )}
        >
          {label}
        </span>
        <WorkflowRunStatusBadge status={instance.status} size="sm" />
      </div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <span className="shrink-0">{duration}</span>
        {isFailed && instance.error !== null && instance.error !== '' ? (
          <span className="min-w-0 truncate text-destructive">
            · {instance.error}
          </span>
        ) : null}
      </div>
    </button>
  );
};

const WorkflowRunStepInstancesListInner: React.FC<{
  instances: readonly WorkflowRunStepInstancePayload[];
  selectedInstanceKey: string | null;
  onSelect: (instanceKey: string) => void;
}> = ({ instances, selectedInstanceKey, onSelect }) => {
  const [statusFilter, setStatusFilter] = useState<string>(
    INSTANCE_STATUS_FILTER_ALL
  );
  const [search, setSearch] = useState('');

  // The counts and the filter describe the iterations, not the "All items"
  // aggregate row (the for-each parent), so a 3-item fan-out reads as 3 — not 4.
  const itemCount = useMemo(
    () =>
      instances.filter((instance) => instance.instanceKey !== ROOT_INSTANCE_KEY)
        .length,
    [instances]
  );

  const { statusCounts } = useMemo(
    () =>
      summarizeFanOutStatuses(
        instances
          .filter((instance) => instance.instanceKey !== ROOT_INSTANCE_KEY)
          .map((instance) => instance.status)
      ),
    [instances]
  );

  // Labels need the full list (sibling indexing), so compute once up front.
  const labeled = useMemo<LabeledInstance[]>(
    () =>
      instances.map((instance, index) => ({
        instance,
        label: labelForStepInstance(instance, index, instances),
      })),
    [instances]
  );

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      labeled.filter(({ instance, label }) => {
        const isAggregate = instance.instanceKey === ROOT_INSTANCE_KEY;
        // The aggregate row is a summary, not an iteration: it only shows in the
        // unfiltered "All" view, and is never matched against a status filter.
        const statusOk = isAggregate
          ? statusFilter === INSTANCE_STATUS_FILTER_ALL
          : statusFilter === INSTANCE_STATUS_FILTER_ALL ||
            instance.status === statusFilter;
        const searchOk = query === '' || label.toLowerCase().includes(query);
        return statusOk && searchOk;
      }),
    [labeled, statusFilter, query]
  );

  // Iterations shown (excludes the aggregate), for the "{shown} of {total}" count.
  const itemsShown = filtered.filter(
    (row) => row.instance.instanceKey !== ROOT_INSTANCE_KEY
  ).length;

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => INSTANCE_ROW_HEIGHT,
    overscan: 10,
  });

  return (
    <div className="flex flex-col gap-2">
      {/* Title, count and filter sit inline on one header row. */}
      <div className="flex items-center justify-between gap-2">
        <Text.Caption className="text-muted-foreground block">
          Instances
        </Text.Caption>
        <div className="flex items-center gap-2">
          <Text.Caption className="text-muted-foreground block tabular-nums">
            {itemsShown === itemCount
              ? `${itemCount}`
              : `${itemsShown} of ${itemCount}`}
          </Text.Caption>
          <InstanceStatusFilter
            statusCounts={statusCounts}
            total={itemCount}
            active={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
      </div>

      {instances.length > SEARCH_VISIBLE_THRESHOLD ? (
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search instances"
            className="border-border/60 bg-background placeholder:text-muted-foreground focus-visible:ring-ring h-7 w-full rounded-md border pl-8 pr-2.5 text-sm outline-none focus-visible:ring-2"
          />
        </div>
      ) : null}

      {/* Capped + internally scrollable: the agent step view renders this in a
          shrink-0 header inside an overflow-hidden column, so an uncapped list
          gets clipped and pushes the conversation off-screen. Virtualized so a
          fan-out of 300+ iterations stays responsive. */}
      {filtered.length === 0 ? (
        <div className="rounded-md border border-border/60 px-2.5 py-6 text-center text-sm text-muted-foreground">
          No instances match.
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="max-h-72 overflow-y-auto overscroll-y-contain rounded-md border border-border/60 p-1"
        >
          <div
            className="relative w-full"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = filtered[virtualRow.index];
              if (row === undefined) {
                return null;
              }
              // The "All items (N)" aggregate row (the for-each parent) is a
              // different kind of row from the individual iterations, so when it
              // sits above item rows it gets a divider to set it apart.
              const isAggregate =
                row.instance.instanceKey === ROOT_INSTANCE_KEY &&
                filtered.length > 1;
              return (
                // Vertical padding on the wrapper gives the gap between rows; the
                // button is inset and rounded, so hover is a rounded pill rather
                // than a band spanning edge-to-edge (the pre-virtualization look).
                <div
                  key={row.instance.instanceKey}
                  className={cn(
                    'absolute left-0 top-0 w-full py-0.5',
                    isAggregate ? 'border-b border-border/60' : ''
                  )}
                  style={{
                    height: INSTANCE_ROW_HEIGHT,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <InstanceRow
                    row={row}
                    selected={row.instance.instanceKey === selectedInstanceKey}
                    onSelect={onSelect}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Shared instance picker for multi-instance run steps (for-each fan-out).
 *
 * A thin guard around {@link WorkflowRunStepInstancesListInner} so the hooks the
 * inner list uses (filter/search state, the virtualizer) are never called for a
 * single-instance step — the list only exists when a step actually fanned out.
 */
export const WorkflowRunStepInstancesList: React.FC<{
  instances: readonly WorkflowRunStepInstancePayload[];
  selectedInstanceKey: string | null;
  onSelect: (instanceKey: string) => void;
}> = (props) => {
  if (props.instances.length <= 1) {
    return null;
  }
  return <WorkflowRunStepInstancesListInner {...props} />;
};

/** Local selection state for a step's instance list. */
export function useStepInstanceSelection(
  instances: readonly WorkflowRunStepInstancePayload[] | undefined,
  initialInstanceKey?: string
): {
  selectedInstanceKey: string | null;
  setSelectedInstanceKey: (key: string) => void;
  selectedInstance: WorkflowRunStepInstancePayload | null;
} {
  const list = useMemo(() => instances ?? [], [instances]);
  const [selectedInstanceKey, setSelectedInstanceKey] = useState<string | null>(
    () => {
      if (
        initialInstanceKey !== undefined &&
        list.some((instance) => instance.instanceKey === initialInstanceKey)
      ) {
        return initialInstanceKey;
      }
      return defaultSelectedInstanceKey(list);
    }
  );

  useEffect(() => {
    if (
      selectedInstanceKey !== null &&
      list.some((instance) => instance.instanceKey === selectedInstanceKey)
    ) {
      return;
    }
    if (
      initialInstanceKey !== undefined &&
      list.some((instance) => instance.instanceKey === initialInstanceKey)
    ) {
      setSelectedInstanceKey(initialInstanceKey);
      return;
    }
    setSelectedInstanceKey(defaultSelectedInstanceKey(list));
  }, [list, selectedInstanceKey, initialInstanceKey]);

  const selectedInstance =
    list.find((instance) => instance.instanceKey === selectedInstanceKey) ??
    null;

  return {
    selectedInstanceKey,
    setSelectedInstanceKey,
    selectedInstance,
  };
}
